import { Image } from "@repo/components/src/ui/image";
import React from "react";

const VIDEO_EXTENSIONS = [".mp4", ".mov", ".m4v", ".webm", ".avi", ".mkv"];

// Seeking a hair past the start: exactly 0s leaves some encodes on a black
// leader frame, so the tile would render as an empty black box.
const FIRST_FRAME_FRAGMENT = "#t=0.1";

export const isVideoUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const path = url.split("?")[0]?.toLowerCase() ?? "";
  return VIDEO_EXTENSIONS.some((extension) => path.endsWith(extension));
};

type Props = {
  url: string | null | undefined;
  alt: string;
};

/**
 * Still preview for a deliverable's media.
 *
 * Self-reported deliverables have no Instagram thumbnail (the content is gone
 * by the time the metrics are reported), so the card falls back to the
 * approved ContentApproval file — which is the creator's *video*. Rendering
 * that through `Image` produced a blank tile.
 *
 * This renders from an unbounded list, so it must not mount a video *player*
 * per tile (`useVideoPlayer` + `VideoView` did). A bare `<video
 * preload="metadata">` seeked to the first frame paints that frame without
 * instantiating a player, and — unlike canvas frame-extraction — needs no
 * CORS headers. That matters concretely here: these are CloudFront
 * signed-querystring URLs on a distribution that does not forward `Origin`,
 * so S3 never returns `Access-Control-Allow-Origin` and a canvas read would
 * throw `SecurityError` on every tile.
 *
 * `center` is a web-only Expo app, so the DOM element is safe. NativeWind
 * `className` does not apply to raw DOM elements, hence the inline style.
 */
const ContentPoster = React.memo(({ url, alt }: Props) => {
  if (!url) return null;

  if (isVideoUrl(url)) {
    return (
      <video
        src={`${url}${FIRST_FRAME_FRAGMENT}`}
        preload="metadata"
        muted
        playsInline
        aria-label={alt}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    );
  }

  return (
    <Image
      source={{ uri: url }}
      alt={alt}
      size="none"
      className="w-full h-full"
      contentFit="cover"
      contentPosition="center"
    />
  );
});

ContentPoster.displayName = "ContentPoster";

export default ContentPoster;
